import {ReadOnlyFieldset} from '@/components';
import {ComplianceStep} from '@/features/trust-accounts/activation/config';
import {
  useCompliancePackageQuery,
  useTrustDetailsPackageQuery
} from '@/features/trust-accounts/activation/hooks';
import {useCurrentTrustAccount} from '@/features/trust-accounts/providers';

import {
  FatcaCrsDeclarationStepForm,
  FatcaCrsSupplementaryStepForm,
  SupportingDocsStepForm
} from './components';

import type {FormStateSnapshot, StepStatus} from '@/hooks';
import type {FC} from 'react';

type TrustAccountActivationComplianceFormProps = {
  trustAccountId: string;
  activeStep: ComplianceStep;
  readOnly?: boolean;
  documentsReadOnly?: boolean;
  transactionLegalDocumentsReadOnly?: boolean;
  onFormStateChange?: (state: FormStateSnapshot) => void;
  onStepStatus?: (step: string, status: StepStatus) => void;
  onGoToSellerStep?: () => void;
  onGoToFatcaCrsStep?: () => void;
};

export const TrustAccountActivationComplianceForm: FC<
  TrustAccountActivationComplianceFormProps
> = ({
  trustAccountId,
  activeStep,
  readOnly = false,
  documentsReadOnly,
  transactionLegalDocumentsReadOnly,
  onFormStateChange,
  onStepStatus,
  onGoToSellerStep,
  onGoToFatcaCrsStep
}) => {
  const complianceQuery = useCompliancePackageQuery(trustAccountId);
  const trustDetailsQuery = useTrustDetailsPackageQuery(trustAccountId);
  const {name: projectName, lawyerName: rawLawyerName} = useCurrentTrustAccount();
  const lawyerName = rawLawyerName ?? undefined;

  const latestDeclaration = complianceQuery.data?.fatcaCrs.latest ?? null;
  const latestSupplementaryDeclaration = complianceQuery.data?.fatcaCrs.latestSupplementary ?? null;
  const sellers = trustDetailsQuery.data?.data.sellers ?? [];

  const isLoading = complianceQuery.isLoading || trustDetailsQuery.isLoading;
  const isError = complianceQuery.isError || trustDetailsQuery.isError;

  const makeStepHandler = (step: ComplianceStep) => (state: FormStateSnapshot) => {
    onStepStatus?.(step, state);
    if (activeStep === step) onFormStateChange?.(state);
  };

  return (
    <div className="flex flex-col gap-6">
      <ReadOnlyFieldset readOnly={readOnly}>
        <div hidden={activeStep !== ComplianceStep.FATCA_CRS}>
          <FatcaCrsDeclarationStepForm
            trustAccountId={trustAccountId}
            declaration={latestDeclaration}
            isLoading={isLoading}
            isError={isError}
            projectName={projectName}
            lawyerName={lawyerName}
            onFormStateChange={makeStepHandler(ComplianceStep.FATCA_CRS)}
          />
        </div>

        <div hidden={activeStep !== ComplianceStep.SUPPLEMENTARY}>
          <FatcaCrsSupplementaryStepForm
            trustAccountId={trustAccountId}
            sellers={sellers}
            latestMainDeclaration={latestDeclaration}
            latestSupplementaryDeclaration={latestSupplementaryDeclaration}
            isLoading={isLoading}
            isError={isError}
            projectName={projectName}
            lawyerName={lawyerName}
            onFormStateChange={makeStepHandler(ComplianceStep.SUPPLEMENTARY)}
            onGoToSellerStep={onGoToSellerStep}
            onGoToFatcaCrsStep={onGoToFatcaCrsStep}
          />
        </div>
      </ReadOnlyFieldset>

      <div hidden={activeStep !== ComplianceStep.SUPPORTING_DOCUMENTS}>
        <SupportingDocsStepForm
          trustAccountId={trustAccountId}
          documentsReadOnly={documentsReadOnly}
          transactionLegalDocumentsReadOnly={transactionLegalDocumentsReadOnly}
          onFormStateChange={makeStepHandler(ComplianceStep.SUPPORTING_DOCUMENTS)}
        />
      </div>
    </div>
  );
};
